import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { api } from '../../lib/api';

/*
 * Campo de destinatarios del redactor (Para / Cc / Cco): el mismo <input> de texto con comas de
 * siempre, más dos ayudas.
 *
 *   · Sugerencias: al escribir, la palabra que se está tecleando se busca en el directorio
 *     (GET /directory: grupos, compañeros y contactos). Flechas + Enter o clic la completan.
 *   · Grupos: si en el campo hay una dirección de grupo («todos@…»), una etiqueta dice cuántas
 *     personas lo recibirán. El reparto lo hace el servidor al enviar (claude/grupos.md): la
 *     cabecera sigue diciendo «todos@…».
 *
 * Todo lo demás (props del input, data-testid) pasa tal cual: el E2E no nota la diferencia.
 */

const ultimaParte = (valor) => {
  const i = Math.max(valor.lastIndexOf(','), valor.lastIndexOf(';'));
  return { antes: i >= 0 ? valor.slice(0, i + 1) : '', parte: valor.slice(i + 1).trim() };
};

const direcciones = (valor) => valor.split(/[,;]/).map((x) => {
  const m = x.match(/<([^>]+)>/);
  return (m ? m[1] : x).trim().toLowerCase();
}).filter(Boolean);

export default function CampoDestinatarios({ value, onChange, style, ...resto }) {
  const [sugerencias, setSugerencias] = useState([]);
  const [activa, setActiva] = useState(0);
  const [abierto, setAbierto] = useState(false);
  const campo = useRef(null);
  const { parte } = ultimaParte(value || '');

  // Los grupos cambian poco: una consulta compartida entre los tres campos y los redactores abiertos
  const { data: grupos = [] } = useQuery({
    queryKey: ['grupos'],
    queryFn: async () => (await api.get('/grupos')).data.grupos || [],
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!abierto || parte.length < 1 || (parte.includes('@') && parte.split('@')[1]?.includes('.'))) {
      setSugerencias([]);
      return undefined;
    }
    let vivo = true;
    const t = setTimeout(() => {
      api.get('/directory', { params: { q: parte } })
        .then(({ data }) => {
          if (!vivo) return;
          const ya = new Set(direcciones(value || ''));
          const items = (data.items || []).filter((i) => i.email && !ya.has(i.email.toLowerCase())).slice(0, 7);
          setSugerencias(items);
          setActiva(0);
        })
        .catch(() => vivo && setSugerencias([]));
    }, 160);
    return () => { vivo = false; clearTimeout(t); };
  }, [parte, abierto, value]);

  const elegir = (item) => {
    const { antes } = ultimaParte(value || '');
    const prefijo = antes ? `${antes.replace(/\s*$/, '')} ` : '';
    onChange({ target: { value: `${prefijo}${item.email}, ` } });
    setSugerencias([]);
    campo.current?.focus();
  };

  const alTeclear = (e) => {
    if (sugerencias.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiva((a) => (a + 1) % sugerencias.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiva((a) => (a - 1 + sugerencias.length) % sugerencias.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); elegir(sugerencias[activa]); return; }
      if (e.key === 'Escape') { e.stopPropagation(); setSugerencias([]); return; }
    }
    resto.onKeyDown?.(e);
  };

  const usados = useMemo(() => {
    const puestas = new Set(direcciones(value || ''));
    return grupos.filter((g) => g.correo && puestas.has(g.correo.toLowerCase()));
  }, [grupos, value]);
  const personas = usados.reduce((n, g) => n + (g.miembros_n || 0), 0);

  return (
    <span style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        {...resto}
        ref={campo}
        value={value}
        onChange={onChange}
        onKeyDown={alTeclear}
        onFocus={(e) => { setAbierto(true); resto.onFocus?.(e); }}
        onBlur={(e) => { setTimeout(() => setAbierto(false), 150); resto.onBlur?.(e); }}
        style={{ ...style, flex: 1, minWidth: 0 }}
        role="combobox"
        aria-expanded={sugerencias.length > 0}
        aria-autocomplete="list"
      />
      {usados.length > 0 && (
        <span
          className="shrink-0 inline-flex items-center gap-1"
          style={{ fontSize: 12, color: 'var(--accent)', background: 'var(--accent-soft)', borderRadius: 999, padding: '2px 8px' }}
          title={`${usados.map((g) => `${g.correo}: grupo ${g.nombre}`).join('\n')}\nLo recibirá cada miembro; la cabecera muestra la dirección del grupo.`}
          data-testid="composer-grupo"
        >
          <Users className="h-3.5 w-3.5" aria-hidden />
          {usados.length === 1 ? usados[0].nombre : `${usados.length} grupos`} · {personas} {personas === 1 ? 'persona' : 'personas'}
        </span>
      )}
      {abierto && sugerencias.length > 0 && (
        <ul className="gmail-from-menu" role="listbox" aria-label="Sugerencias" style={{ listStyle: 'none', margin: 0 }}>
          {sugerencias.map((s, i) => (
            <li key={`${s.source}:${s.id}`}>
              <button
                type="button"
                role="option"
                aria-selected={i === activa}
                className={`gmail-from-option ${i === activa ? 'gmail-from-option--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); elegir(s); }}
              >
                {s.source === 'group' && <Users className="h-4 w-4 shrink-0" style={{ color: 'var(--accent)' }} aria-hidden />}
                <span className="gmail-from-option-body">
                  <span style={{ display: 'block', fontSize: 13.5, color: 'var(--text)', fontWeight: 600 }} className="truncate">{s.name}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-3)' }} className="truncate">
                    {s.email}{s.source === 'group' ? ` · grupo de ${s.miembros_n} ${s.miembros_n === 1 ? 'persona' : 'personas'}` : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
